import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Typography
} from "@mui/material";
import {HighlightOff} from "@mui/icons-material";
import React, {useEffect} from "react";
import isEqual from "lodash.isequal";
import EnhancedErrorDialog from "../../../../../../components/EnhancedErrorDialog";
import { pageContentGap, DialogHeader } from '../../../../../../theme';

interface ErrorState {
    open?: boolean;
    errorHeader?: string;
    errorMessage?: string;
    [key: string]: any;
}

interface SettingsDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    isEdit: boolean;
    label: React.ReactNode;
    subtitle?: React.ReactNode;
    noPrefix?: boolean;
    disabled?: boolean;
    error: ErrorState;
    setError: (error: ErrorState) => void;
    inputStyles?: React.CSSProperties;
    contentStyles?: React.CSSProperties;
    paperStyles?: React.CSSProperties;
    newEntity: any;
    initEntity: any;
    loading: boolean;
    loadingHeader?: React.ReactNode;
    loadingMessage?: React.ReactNode;
    update: () => void;
    create: () => void;
    inputs?: React.ReactNode;
    maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
}

export default function SettingsDialog(props: SettingsDialogProps) {
    const {open, setOpen, isEdit} = props;
    const [isDirty, setIsDirty] = React.useState(false);

    useEffect(() => {
        if (isEqual(props.newEntity, props.initEntity)) setIsDirty(false);
        else setIsDirty(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.newEntity]);

    return (
        <>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth={props.maxWidth || "lg"}
                    fullWidth={props.fullWidth}
                    slotProps={{
                        paper: {
                            style: {
                                borderRadius: "12px",
                                minHeight: "100px",
                                maxHeight: "80vh", ...props.paperStyles
                            }
                        }
                    }}
            >
                <DialogTitle>
                    <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 0.5, flexDirection: "column"}}>
                            <DialogHeader>
                                {props.noPrefix ? props.label : isEdit ? `Edit ${props.label}` : `New ${props.label}`}
                            </DialogHeader>

                            <Typography style={{fontSize: "12px", fontWeight: 300}}>
                                {props.subtitle}
                            </Typography>
                        </Grid>

                        <IconButton onClick={() => setOpen(false)} size="large">
                            <HighlightOff style={{color: "red"}}/>
                        </IconButton>
                    </Grid>
                </DialogTitle>

                <DialogContent>
                    <Grid container
                          style={{width: "600px", gap: pageContentGap, flexDirection: "column", ...props.contentStyles}}>
                        <Grid container style={{
                            // maxHeight: "600px",
                            height: "calc(100% - 140px)",
                            minHeight: "200px",
                            gap: pageContentGap,
                            flexDirection: "column",
                            flexWrap: "nowrap",
                            overflow: "auto",
                            ...props.inputStyles
                        }}>
                            {props.inputs}
                        </Grid>
                    </Grid>

                    <Dialog open={props.loading} onClose={(event, reason) => {
                      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    }}>
                        <LinearProgress
                            sx={{
                                width: "100%",
                                maxWidth: "100vw",
                                height: '0.2em',
                                visibility: props.loading ? undefined : 'hidden'
                            }}/>

                        <div style={{display: "flex", flexDirection: "column", gap: pageContentGap, padding: "24px"}}>
                            <Typography variant={"h6"}>{props.loadingHeader}</Typography>
                            <Typography variant={"body1"}>{props.loadingMessage}</Typography>
                        </div>
                    </Dialog>
                </DialogContent>

                <DialogActions
                    style={{
                        padding: "12px 24px 24px 24px",
                    }}
                >
                    <Grid item container justifyContent={"flex-end"} style={{gap: "8px"}}>
                        {!props.disabled && (
                            <>
                                <Button variant="text" onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>

                                <Button variant="text" color="primary" data-testid={isEdit ? "settings-dialog-save" : "settings-dialog-add"} disabled={!isDirty} onClick={() => isEdit ? props.update() : props.create()}>
                                    {isEdit ? "Save" : "Add"}
                                </Button>
                            </>
                        )}
                    </Grid>
                </DialogActions>
            </Dialog>
            <EnhancedErrorDialog open={props.error.open} onClose={() => props.setError({...props.error, open: false})}
                                 errorHeader={props.error.errorHeader} errorMessage={props.error.errorMessage}/>
        </>
    );
}
